import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { cardService } from '@/services/cardService';
import { toast } from 'sonner';

export interface PaymentMethod {
  id: string;
  id_stripe: string;
  type: string;
  last4: string;
  holder: string;
  expiry_month: number;
  expiry_year: number;
  is_default: boolean;
  brand: string;
}

export const usePaymentMethods = (idStripeClient: string | undefined, skipInitialFetch = false) => {
  useLanguage();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState<PaymentMethod | null>(null);

  const fetchCards = async () => {
    if (!idStripeClient) return;

    setIsLoading(true);
    try {
      const response = await cardService.getAllUserCard(idStripeClient);

      if (response.success && response.data) {
        // Mapear os dados da API para o formato esperado
        const mappedCards = response.data.map((card: any) => ({
          id: card.id_Stripe_Card,
          id_stripe: card.id_Stripe_Card,
          type: card.funding || 'credit',
          last4: card.last_Digits,
          holder: card.holder_Name || '',
          expiry_month: card.expire_Month,
          expiry_year: card.expire_Year,
          is_default: card.default_Card === true,
          brand: card.brand || 'unknown',
        }));

        setPaymentMethods(mappedCards);

        // Define o cartão padrão ou o primeiro como selecionado
        const defaultCard = mappedCards.find((card: PaymentMethod) => card.is_default) || mappedCards[0];
        setSelectedCard(defaultCard || null);
      } else {
        setPaymentMethods([]);
        setSelectedCard(null);
      }
    } catch (error) {
      console.error('Erro ao buscar cartões:', error);
      toast.error(translateInterfaceText("Erro ao buscar cartões"));
      setPaymentMethods([]);
      setSelectedCard(null);
    } finally {
      setIsLoading(false);
    }
  };

  const setDefaultCard = async (cardId: string) => {
    if (!idStripeClient) return;

    try {
      const response = await cardService.setDefaultCard(idStripeClient, { cardId });

      if (response.success) {
        toast.success(translateInterfaceText("Cartão padrão atualizado!"));
        await fetchCards(); // Recarrega a lista
      } else {
        toast.error(translateInterfaceText("Erro ao definir cartão padrão"));
      }
    } catch (error) {
      console.error('Erro ao definir cartão padrão:', error);
      toast.error(translateInterfaceText("Erro ao definir cartão padrão"));
    }
  };

  const deleteCard = async (cardId: string) => {
    if (!idStripeClient) return;

    try {
      const response = await cardService.deleteCard(idStripeClient, cardId);

      if (response.success !== false) {
        toast.success(translateInterfaceText("Cartão removido com sucesso!"));
        await fetchCards(); // Recarrega a lista
      } else {
        toast.error(response.message || translateInterfaceText("Erro ao remover cartão"));
      }
    } catch (error) {
      console.error('Erro ao remover cartão:', error);
      toast.error(translateInterfaceText("Erro ao remover cartão"));
    }
  };

  useEffect(() => {
    if (!skipInitialFetch) fetchCards();
  }, [idStripeClient, skipInitialFetch]);

  return {
    paymentMethods,
    isLoading,
    selectedCard,
    setSelectedCard,
    fetchCards,
    setDefaultCard,
    deleteCard,
  };
};
